import React, {FC, PropsWithChildren} from 'react';
import styled, {css, DefaultTheme} from 'styled-components';

import {RendererOf} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {blendColors} from '../../../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {ThemeControllerProvider} from '../../../../../../libs/shared/ui/src/theme/themeControllerProvider';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {isDarkTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypeHelpers';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypes';

/*
 * Props.
 */

interface MessageBaseProps {
  renderBanner?: RendererOf<RenderBannerOptions>;
  isPopout?: boolean;
  isPopup?: boolean;
  isFaded?: boolean;
  isImageMessage?: boolean;
  timelineVariation: TimelineVariationsEnum;
  isInbound?: boolean;
  wasJustHighlighted?: boolean;
}

interface RenderBannerOptions {
  isRounded: boolean;
}

/*
 * Style.
 */

interface WrapperDivStyleProps {
  $isPopout: boolean;
  $isPopup: boolean;
  $isFaded?: boolean;
  $isImageMessage?: boolean;
  $isInbound?: boolean;
  $wasJustHighlighted?: boolean;
}
const StyledWrapperDiv = styled.div<WrapperDivStyleProps>`
  display: flex;
  flex-direction: column;
  flex: 1;

  background-color: ${(p) => getBackgroundColor(p, p.theme)};
  transition: background-color 0.5s ease-in-out;

  border-radius: ${(p) => getBorderRadius(p)}px;

  ${(p) => maybeAddFadedStyle(p.$isFaded, p.$isImageMessage, p.$wasJustHighlighted)};

  min-height: 42px;
`;

/** If we're faded, unfade on hover */
function maybeAddFadedStyle(isFaded?: boolean, isImageMessage?: boolean, wasJustHighlighted?: boolean) {
  if (!isFaded || isImageMessage) {
    return '';
  }

  if (wasJustHighlighted) {
    return css`
      &:hover {
        border-color: ${(p) => p.theme.alphas.black10};
        background-color: ${(p) => blendColors(p.theme.palette.blue.shade20, p.theme.alphas.gray10)};
      }
    `;
  }

  return css`
    /* Remove transitions for collapsed message to avoid animating hover. */
    transition: none;
    &:hover {
      border-color: ${(p) => p.theme.alphas.black10};
      background-color: ${(p) => blendColors(p.theme.greys.shade20, p.theme.alphas.gray10)};
    }
  `;
}

function getBackgroundColor(props: WrapperDivStyleProps, theme: DefaultTheme): string {
  if (props.$isImageMessage) {
    return '';
  }

  if (props.$wasJustHighlighted) {
    return theme.message.highlightedBackground;
  }

  return theme.message.background;
}

function getBorderRadius(props: WrapperDivStyleProps) {
  if (props.$isPopout) {
    return 0;
  }

  return 16;
}

function addShadow(props: WrapperDivStyleProps, theme: UserTheme) {
  if (props.$isPopout || props.$isPopup || props.$isImageMessage || isDarkTheme(theme)) {
    return '';
  }

  return css`
    border: 1px solid ${(p) => p.theme.alphas.black20};
    ${(p) => boxShadow(p.theme, `0 1px 6px ${p.theme.alphas.black20}, 0 4px 30px rgba(0, 0, 0, 0)`)};
  `;
}

interface StyledContentDivProps {
  $hasBanner: boolean;
  $timelineVariation: TimelineVariationsEnum;
}
const StyledContentDiv = styled.div<StyledContentDivProps & WrapperDivStyleProps>`
  display: flex;
  flex-direction: column;
  flex: 1;

  background-color: ${(p) =>
    p.$hasBanner && !p.$isImageMessage ? p.theme.message.background : 'transparent'};

  min-height: 42px;
  border-radius: 16px;

  ${(p) => addShadow(p, p.theme)};
`;

/*
 * Component.
 */

export const MessageBase: FC<PropsWithChildren<MessageBaseProps>> = (props) => {
  const {
    renderBanner,
    children,
    isPopout = false,
    isPopup = false,
    isImageMessage,
    timelineVariation,
    isInbound,
  } = props;
  const banner = renderBanner && renderBanner({isRounded: !isPopout});

  return (
    <ThemeControllerProvider>
      <StyledWrapperDiv
        $isPopout={isPopout}
        $isPopup={isPopup}
        $isFaded={props.isFaded}
        $isImageMessage={isImageMessage}
        $isInbound={isInbound}
        $wasJustHighlighted={props.wasJustHighlighted}
      >
        {banner}
        <StyledContentDiv
          $isPopout={isPopout || false}
          $isPopup={isPopup || false}
          $isFaded={props.isFaded}
          $isImageMessage={isImageMessage}
          $hasBanner={Boolean(banner)}
          $timelineVariation={timelineVariation}
          $isInbound={isInbound}
        >
          {children}
        </StyledContentDiv>
      </StyledWrapperDiv>
    </ThemeControllerProvider>
  );
};
